import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTooth, 
  faTeeth, 
  faMagic, 
  faXRay, 
  faTasks, 
  faHeart,
  faSpa
} from '@fortawesome/free-solid-svg-icons';

const Services: React.FC = () => {
  const services = [
    {
      id: 'soins',
      title: 'Soins dentaires',
      description: 'Notre cabinet propose une gamme complète de soins dentaires pour maintenir votre santé bucco-dentaire. Nous effectuons des traitements des caries, des dévitalisations, des détartrages et des soins préventifs. Notre équipe utilise des techniques modernes et des matériaux de haute qualité pour garantir des soins durables et confortables.',
      icon: faTooth,
      image: '/images/soins.jpg',
      details: [
        'Traitement des caries avec des matériaux esthétiques',
        'Dévitalisation (traitement de canal)',
        'Détartrage et polissage professionnel',
        'Traitement de la sensibilité dentaire',
        'Restaurations dentaires en composite',
        'Soins préventifs et conseils d\'hygiène'
      ]
    },
    {
      id: 'chirurgie',
      title: 'Chirurgie dentaire',
      description: 'Notre équipe de chirurgiens dentaires expérimentés réalise diverses interventions chirurgicales avec précision et expertise. Nous utilisons des techniques modernes et des équipements de pointe pour assurer des interventions sûres et confortables, avec un suivi post-opératoire attentif.',
      icon: faTeeth,
      image: '/images/chirurgie.jpg',
      details: [
        'Extractions dentaires simples et complexes',
        'Extractions des dents de sagesse',
        'Chirurgie pré-implantaire',
        'Biopsies des tissus buccaux',
        'Traitement des infections dentaires',
        'Chirurgies apicales'
      ]
    },
    {
      id: 'dentisterie-esthetique',
      title: 'Dentisterie esthétique',
      description: 'Transformez votre sourire avec nos solutions esthétiques personnalisées. Nous proposons des traitements adaptés à vos besoins spécifiques pour obtenir un sourire naturel et éclatant. Notre approche combine expertise technique et sens artistique pour des résultats harmonieux.',
      icon: faMagic,
      image: '/images/esthetique.jpg',
      details: [
        'Blanchiment dentaire professionnel',
        'Facettes dentaires en porcelaine',
        'Couronnes esthétiques',
        'Restaurations en composite',
        'Correction des dents ébréchées',
        'Design du sourire'
      ]
    },
    {
      id: 'radiographie',
      title: 'Radiographie',
      description: 'Notre cabinet est équipé de technologies d\'imagerie dentaire de pointe pour un diagnostic précis. Nous utilisons des équipements numériques modernes qui réduisent l\'exposition aux radiations tout en fournissant des images de haute qualité pour une meilleure planification des traitements.',
      icon: faXRay,
      image: '/images/radiographie.jpg',
      details: [
        'Radiographies panoramiques',
        'Radiographies rétro-alvéolaires',
        'Imagerie 3D (Cone Beam)',
        'Diagnostic précis des problèmes dentaires',
        'Planification des traitements complexes',
        'Suivi de l\'évolution des soins'
      ]
    },
    {
      id: 'implantologie',
      title: 'Implantologie',
      description: 'Remplacez vos dents manquantes par des solutions durables et naturelles. Nos implants dentaires sont conçus pour s\'intégrer parfaitement à votre dentition existante, offrant une fonction masticatoire optimale et un aspect esthétique naturel. Nous utilisons des implants de haute qualité et des techniques chirurgicales avancées.',
      icon: faTasks,
      image: '/images/implant.jpg',
      details: [
        'Pose d\'implants dentaires',
        'Couronnes sur implants',
        'Bridges sur implants',
        'Prothèses complètes sur implants',
        'Greffes osseuses',
        'Réhabilitation complète'
      ]
    },
    {
      id: 'parodontologie',
      title: 'Parodontologie',
      description: 'Préservez la santé de vos gencives et protégez vos dents avec nos traitements parodontaux spécialisés. Nous proposons des solutions personnalisées pour traiter les maladies gingivales et maintenir la santé de vos tissus de soutien dentaire.',
      icon: faHeart,
      image: '/images/parodontologie.jpg',
      details: [
        'Traitement des maladies gingivales',
        'Traitement de la parodontite',
        'Curetage et surfaçage radiculaire',
        'Chirurgie des gencives',
        'Maintenance parodontale',
        'Contrôle des infections buccales'
      ]
    },
    {
      id: 'orthodontie',
      title: 'Orthodontie',
      description: 'Alignez vos dents pour un sourire harmonieux et une meilleure fonction masticatoire. Nous proposons des solutions orthodontiques adaptées à tous les âges, des appareils traditionnels aux techniques invisibles, pour obtenir des résultats optimaux avec un maximum de confort.',
      icon: faSpa,
      image: '/images/orthodontie.jpg',
      details: [
        'Appareils dentaires traditionnels',
        'Orthodontie invisible',
        'Correction des malocclusions',
        'Traitement des problèmes d\'alignement',
        'Rétention post-traitement',
        'Solutions pour enfants et adultes'
      ]
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <div className="bg-accent text-white pb-20 relative overflow-hidden" style={{ paddingTop: 'calc(5rem + var(--announcement-offset, 0px))' }}>
        <div className="absolute inset-0 bg-gradient-to-b from-accent to-black opacity-80"></div>
        <div className="relative z-10 section-container text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Nos Services</h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Découvrez notre gamme complète de services dentaires, conçus pour répondre à tous vos besoins en matière de santé bucco-dentaire.
          </p>
        </div>
      </div>

      {/* Services Grid */}
      <div className="section-container py-16">
        <div className="space-y-24">
          {services.map((service, index) => (
            <div 
              key={service.id}
              className={`bg-white rounded-xl shadow-card overflow-hidden ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="relative h-64 lg:h-full">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-accent/80 to-transparent lg:hidden"></div>
                </div>
                <div className="p-8 lg:p-12">
                  <div className="flex flex-col items-center mb-8">
                    <div className="h-16 w-16 bg-primary rounded-full flex items-center justify-center mb-4">
                      <FontAwesomeIcon icon={service.icon} className="h-8 w-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-accent text-center">{service.title}</h2>
                  </div>
                  <p className="text-gray-600 mb-8 text-lg leading-relaxed text-left">{service.description}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {service.details.map((detail, index) => (
                      <div key={index} className="flex items-start group">
                        <span className="text-primary mr-3 text-lg group-hover:text-accent transition-colors">•</span>
                        <span className="text-gray-700 group-hover:text-gray-900 transition-colors text-left">{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="section-container py-16 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">Besoin d'un conseil personnalisé?</h2>
          <p className="text-lg mb-8">
            Contactez-nous pour discuter de vos besoins spécifiques et obtenir un plan de traitement adapté.
          </p>
          <a 
            href="https://wa.me/212684707407" 
            target="_blank"
            rel="noopener noreferrer"
            className="bg-green-600 text-white hover:bg-green-700 font-medium px-8 py-4 rounded-md transition-colors inline-flex items-center"
          >
            <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Contacter via WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
};

export default Services; 